/**
 * Prüfung bearbeiteter normaler Regeln (isomorph): dieselben Meldungen im Editor und in der API.
 * Die Form legt core fest (`NormalRule`); hier nur, welche Angaben je Regelart nötig sind.
 */
import type { NormalRule } from '@pat/core/rules';
import { exprIssue, hasUnsupported } from './search-text.js';

export const RULE_KINDS = [
  'same-store-category',
  'same-store-main-category',
  'same-ebay-category',
  'same-ebay-main-category',
  'fixed-store-category',
  'fixed-ebay-category',
  'title-search',
  'group',
] as const satisfies readonly NormalRule['kind'][];

export type RuleShape = 'none' | 'categories' | 'search';

export function ruleShape(kind: NormalRule['kind']): RuleShape {
  if (kind === 'fixed-store-category' || kind === 'fixed-ebay-category') return 'categories';
  if (kind === 'title-search' || kind === 'group') return 'search';
  return 'none';
}

/**
 * Neue Regeln im Editor höchstens bis hierher; importierte Galerien mit mehr Regeln (bis 1.925 im
 * Bestand, core erlaubt 2.500) bleiben bearbeitbar, dürfen aber nicht weiter wachsen.
 */
export const MAX_EDITOR_RULES = 200;
/** Obergrenze laut core (`RuleSet.rules`). */
export const MAX_RULES = 2500;

export function ruleLimit(existingCount: number): number {
  return Math.min(MAX_RULES, Math.max(MAX_EDITOR_RULES, existingCount));
}

const CATEGORY_ID = /^[1-9][0-9]{0,19}$/;

export function ruleIssue(rule: NormalRule): string | undefined {
  const shape = ruleShape(rule.kind);
  if (shape === 'none' && (rule.categoryIds !== undefined || rule.source || rule.target)) return 'Diese Regelart hat keine weiteren Angaben';
  if (shape === 'categories') {
    if (!rule.categoryIds?.length) return 'Bitte mindestens eine Kategorie angeben';
    if (rule.categoryIds.some(id => !CATEGORY_ID.test(id))) return 'Kategorienummern bestehen nur aus Ziffern und beginnen nicht mit 0';
    if (rule.source || rule.target) return 'Feste Kategorien haben keine Suchbegriffe';
  }
  if (shape === 'search') {
    // Wie die Engine: Titelsuche braucht nur das Ziel (ohne Ausgang gilt sie für jedes Angebot), Gruppen beides.
    if (!rule.target) return 'Bitte Zielbegriffe angeben';
    if (rule.kind === 'group' && !rule.source) return 'Bitte Ausgangsbegriffe angeben';
    if (rule.categoryIds !== undefined) return 'Suchregeln haben keine Kategorien';
  }
  if (!hasUnsupported(rule.source) && !hasUnsupported(rule.target)) return exprIssue(rule.source) ?? exprIssue(rule.target);
  return undefined;
}

export interface RulesIssue {
  index: number;
  message: string;
}

/**
 * Ganze Liste: Regelangaben, eindeutige IDs und dass nicht übersetzbare Altbegriffe nur in
 * unveränderten bestehenden Regeln vorkommen (`existing` = gespeicherter Stand).
 */
export function rulesIssues(rules: readonly NormalRule[], existing: readonly NormalRule[] = []): RulesIssue[] {
  const issues: RulesIssue[] = [];
  const seen = new Set<string>();
  const before = new Map(existing.map(rule => [rule.ruleId, JSON.stringify(rule)]));
  const limit = ruleLimit(existing.length);
  if (rules.length > limit) issues.push({ index: limit, message: `Höchstens ${limit} Regeln` });
  rules.forEach((rule, index) => {
    if (seen.has(rule.ruleId)) issues.push({ index, message: 'Regel doppelt vorhanden' });
    seen.add(rule.ruleId);
    const issue = ruleIssue(rule);
    if (issue) issues.push({ index, message: issue });
    if ((hasUnsupported(rule.source) || hasUnsupported(rule.target)) && before.get(rule.ruleId) !== JSON.stringify(rule)) {
      issues.push({ index, message: 'Nicht übersetzbare Altbegriffe lassen sich nicht ändern; Regel unverändert lassen oder entfernen' });
    }
  });
  return issues;
}
